import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { setLoading } from './LoadingSlice';
import axios from 'axios';
import Unauthorized from '../Admin/Unauthorized';
import { toast } from 'react-toastify';
import BaseUrl from '../../BaseUrl';

const API_BASE_URL = BaseUrl.authurl;

// Function to get fresh headers
const getHeaders = () => {
  try {
    return Unauthorized.Actionsauthorized();
  } catch (error) {
    console.error('Error getting headers:', error);
    return {};
  }
};

// Create an axios instance with baseURL
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 second timeout
});

// Add request interceptor to add headers dynamically
apiClient.interceptors.request.use(
  (config) => {
    const headers = getHeaders();
    config.headers = {
      ...config.headers,
      ...headers,
      'Content-Type': 'application/json',
    };
    
    // Check if token exists
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No authentication token found');
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401/403 errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const status = error.response.status;
      if (status === 401 || status === 403) {
        // Don't show toast here as it will be handled by the component
        console.error(`Authentication error (${status}):`, error.response.data);
        // Enhance error with status for better handling
        error.status = status;
      }
    }
    return Promise.reject(error);
  }
);

// Fetch all departments
export const fetchDepartments = createAsyncThunk(
  'departments/fetchDepartments',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await apiClient.get('Departments/GetAllDepartments');
      return response.data;
    } catch (error) {
      console.error('Fetch departments failed:', error.response?.status, error.response?.data);
      
      // Add status to error for better handling
      if (error.response) {
        error.status = error.response.status;
      }
      
      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to fetch departments',
        status: error.response?.status || 500,
      });
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Add new department
export const addNewDepartment = createAsyncThunk(
  'departments/addNewDepartment',
  async (newDepartment, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await apiClient.post('Departments/AddDepartment', newDepartment);
      toast.success('Department added successfully');
      return response.data;
    } catch (error) {
      console.error('Add department failed:', error.response?.status, error.response?.data);
      
      if (error.response) {
        error.status = error.response.status;
      }
      
      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to add department',
        status: error.response?.status || 500,
      });
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Update department
export const updateDepartment = createAsyncThunk(
  'departments/updateDepartment',
  async (updatedDepartment, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await apiClient.put(
        `Departments/UpdateDepartment?DepartmentId=${updatedDepartment.Id}`,
        updatedDepartment
      );
      toast.success('Department updated successfully');
      return response.data;
    } catch (error) {
      console.error('Update department failed:', error.response?.status, error.response?.data);
      
      if (error.response) {
        error.status = error.response.status;
      }
      
      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to update department',
        status: error.response?.status || 500,
      });
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Delete department
export const deleteDepartment = createAsyncThunk(
  'departments/deleteDepartment',
  async (departmentId, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await apiClient.delete('Departments/DeleteDepartment', {
        params: { Id: departmentId },
      });
      toast.success('Department deleted successfully');
      return departmentId;
    } catch (error) {
      console.error('Delete department failed:', error.response?.status, error.response?.data);
      
      if (error.response) {
        error.status = error.response.status;
      }
      
      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to delete department',
        status: error.response?.status || 500,
      });
    } finally {
      dispatch(setLoading(false));
    }
  }
);

const departmentsSlice = createSlice({
  name: 'departments',
  initialState: {
    departments: [],
    status: 'idle',
    error: null,
  },
  reducers: {
    resetDepartmentsState: (state) => {
      state.status = 'idle';
      state.error = null;
      state.departments = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDepartments.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.departments = action.payload || [];
        state.error = null;
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message || action.error?.message || 'Failed to fetch departments';
      })
      .addCase(addNewDepartment.fulfilled, (state, action) => {
        state.departments.push(action.payload);
        state.error = null;
      })
      .addCase(addNewDepartment.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to add department';
      })
      .addCase(updateDepartment.fulfilled, (state, action) => {
        const index = state.departments.findIndex(dept => dept.Id === action.payload.Id);
        if (index !== -1) {
          state.departments[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateDepartment.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to update department';
      })
      .addCase(deleteDepartment.fulfilled, (state, action) => {
        state.departments = state.departments.filter(dept => dept.Id !== action.payload);
        state.error = null;
      })
      .addCase(deleteDepartment.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to delete department';
      });
  },
});

export const { resetDepartmentsState } = departmentsSlice.actions;
export default departmentsSlice.reducer;